import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { httpResource, HttpResourceRef, HttpResourceRequest } from '@angular/common/http';

import { environment } from '@env/environment';
import {
    groupSectionsByBlockId,
    LandingSectionGroup,
    LandingSeo,
} from '@core/cms/landing.cms';
import { LanguageService } from '@core/i18n/language.service';
import { PageGraphqlResponse, TymeGroupPage } from '@core/cms/page.cms';
import { TYME_GROUP_PAGE_QUERY } from '@core/cms/tyme-group-page.query';

/** Strapi publication-status filter accepted by the CMS GraphQL API. */
type PublicationStatus = 'PUBLISHED' | 'DRAFT';

/** Variables for the {@link TYME_GROUP_PAGE_QUERY} `TymeGroupPage` operation. */
interface PageQueryVariables {
    readonly slug: string;
    readonly tenant: string;
    readonly locale: string;
    readonly status: PublicationStatus;
}

/** GraphQL POST body sent to the CMS to resolve a dynamic page. */
interface PageQueryBody {
    readonly operationName: string;
    readonly query: string;
    readonly variables: PageQueryVariables;
}

/**
 * Fetches a single dynamic CMS page (an `article`) by `slug`. Mirrors {@link LandingCmsService} but
 * is keyed by a `slug` signal instead of a single type, so it is **provided per dynamic-page
 * component** (not `root`): each page instance owns its own request and grouped sections.
 *
 * Reactive: refetches when `slug()` or the active language changes, runs during SSR with the result
 * transferred to the client, and exposes the standard `page.value()/isLoading()/error()`.
 */
@Injectable()
export class PageCmsService {
    private readonly _language: LanguageService = inject(LanguageService);
    private readonly _cmsUrl: string = environment.cmsUrl;
    private readonly _tenant: string = environment.tenant;

    /** Active language code; drives the CMS request locale. */
    private readonly _lang: Signal<string> = this._language.lang;

    /** Slug of the page to load; set by the routed component from the `:slug` route parameter. */
    public readonly slug: WritableSignal<string | null> = signal<string | null>(null);

    /**
     * The dynamic page for the current `slug()` + language. Skips the request until a slug is set
     * (or when `cmsUrl` is empty); `parse` takes the first `articles` match, or `null` when none.
     */
    public readonly page: HttpResourceRef<TymeGroupPage | null> =
        httpResource<TymeGroupPage | null>(
            (): HttpResourceRequest | undefined => {
                const slug: string | null = this.slug();

                if (!this._cmsUrl || !this._tenant || !slug) {
                    return undefined;
                }

                const body: PageQueryBody = {
                    operationName: 'TymeGroupPage',
                    query: TYME_GROUP_PAGE_QUERY,
                    variables: {
                        slug,
                        tenant: this._tenant,
                        locale: this._lang(),
                        status: 'PUBLISHED',
                    },
                };

                return {
                    url: `${this._cmsUrl}/graphql`,
                    method: 'POST',
                    headers: { 'accept-language': this._lang() },
                    body,
                };
            },
            {
                defaultValue: null,
                parse: (raw: unknown): TymeGroupPage | null =>
                    (raw as PageGraphqlResponse)?.data?.articles?.[0] ?? null,
            },
        );

    /** Page sections grouped into runs of consecutive entries sharing the same `blockId`. */
    public readonly sectionGroups: Signal<ReadonlyArray<LandingSectionGroup>> = computed(
        (): ReadonlyArray<LandingSectionGroup> =>
            groupSectionsByBlockId(this.page.value()?.sections ?? []),
    );

    /** The page's `shared.seo` component for the active language (`null` until resolved). */
    public readonly seo: Signal<LandingSeo | null> = computed(
        (): LandingSeo | null => this.page.value()?.seo ?? null,
    );

    /** `true` once a slug was requested and resolved but no matching page exists (a 404). */
    public readonly notFound: Signal<boolean> = computed(
        (): boolean =>
            this.slug() !== null &&
            !this.page.isLoading() &&
            !this.page.error() &&
            this.page.value() === null,
    );
}
